import { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Unity, useUnityContext } from 'react-unity-webgl';

import { getDeviceType } from '@/src/components/Common/CommonHooks';
import { useLocalForage } from '@/src/components/Common/hooks';
import { useAdobeLaunch, getPageId } from '@/src/components/Common/utils/adobeLaunch';
import Button from '@/src/components/Common/ui-components/Button';
import { GlobalContext } from '@/src/contexts/GlobalContext';
import { RacingContext } from '@/src/components/Home/RacingPage';

import horseRunAnimation from './img/horse-run.gif';
import errorIcon from './img/error.png';
import errorBlack from './img/errorBlack.svg';
import loadingLogo from './img/Logo3.png';
import styles from './UnityWebGLPopUp.module.scss';

export const UnityWebGLPopUp = () => {
    const { t } = useTranslation();

    const { date, venue, raceNo, raceData } = useParams();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode'); // Simple | Advanced
    const meetingId = searchParams.get('meetingId');
    const distance = searchParams.get('distance');

    const { isDesktop } = getDeviceType();
    const [isStart, setIsStart] = useState(false);
    const { visible, getTriggerProps } = useAutoHide({ disabled: isDesktop, isStart });

    const { unityProvider, sendMessage, isLoaded, loadingProgression } = useUnityContext(getConfig(venue));
    const canvasRef = useRef(null);
    const skipAnimation = raceData.charAt(1) === '1';
    const [showButtons, setShowButtons] = useState({
        last400m: false,
        last200m: false,
        viewResult: false,
        skipReplay: false
    });

    const [errorCode, setErrorCode] = useState(0);

    const { sendAdobeEvent } = useAdobeLaunch();
    const timeoutsRef = useRef([]);
    const startTimeRef = useRef(Date.now());

    const { setValue: setRaceSimStorageValue, loadValue: loadLocalImportanceValue } = useLocalForage(
        'LocalImportanceValue',
        [],
        (value) => {
            try {
                if (value && Array.isArray(value)) {
                    return value; // Data validation was completed during the initial load, so the data can be used directly here.
                }
                return [];
            } catch (error) {}

            return [];
        }
    );

    const saveResultToStorage = useCallback(
        async (result) => {
            const raceSimStorageValue = await loadLocalImportanceValue();
            const value = raceSimStorageValue.find((i) => i?.mid === meetingId);
            if (!value) return;

            const raceDataId = value.para.findIndex((i) => i?.no === parseInt(raceNo));
            if (raceDataId === -1) return;

            value.para[raceDataId] = {
                ...value.para[raceDataId],
                showResultBar: true,
                rsShowPage: 'RS_RESULT',
                ...result
            };
            await setRaceSimStorageValue(raceSimStorageValue);
        },
        [loadLocalImportanceValue, meetingId, raceNo, setRaceSimStorageValue]
    );

    useEffect(() => {
        if (canvasRef.current) {
            const gl = canvasRef.current.getContext('webgl2');
            // For compatibility with vendor solution, adding the following API to `window`
            const vendorAPIs = {
                // the actual fields that are used by the vendor solution, and will convert to listenable properties
                gResult: undefined,
                gStatus: undefined,
                gErrorNo: undefined,
                gRaceReady: undefined,
                // the private, primitive values of the listenable properties above
                _gResult: '',
                _gStatus: 0,
                _gErrorNo: 0,

                texture: gl.createTexture(),

                RaceStarting: (lPara) => sendMessage('GameObject', 'RaceStart', lPara),
                JumpLast200: () => sendMessage('GameObject', 'Jump200', 1),
                JumpLast400: () => sendMessage('GameObject', 'Jump400', 1),
                JumpToResult: () => sendMessage('GameObject', 'JumpResult', 1),
                SkipReplay: () => sendMessage('GameObject', 'SkipReplay', 1),
                EnableBottomButton: () => sendMessage('GameObject', 'DisableButton', false),
                DisableBottomButton: () => sendMessage('GameObject', 'DisableButton', true),

                RacingResult: (param) => {
                    window.gResult = param;
                },
                RacingStatus: (lStatus) => {
                    window.gStatus = lStatus;
                },
                RacingError: (lErrNo) => {
                    window.gErrorNo = lErrNo;
                },
                PutScreen: (RetTex) => {
                    window.texture = RetTex;
                },
                RacingReady: (param) => {
                    window.gRaceReady = param;
                },

                getStatus: () => window.gStatus,
                getResult: () => window.gResult,
                getError: () => window.gErrorNo,
                getResultScreen: () => window.texture,
                getReady: () => window.gRaceReady
            };

            Object.assign(window, vendorAPIs);

            // make the gStatus, gResult, and gErrorNo properties listenable
            Object.defineProperty(window, 'gStatus', {
                get() {
                    return this._gStatus;
                },
                set(value) {
                    if (value === this.gStatus) return;
                    this._gStatus = value;

                    switch (this._gStatus) {
                        case 10: {
                            // race start
                            if (!skipAnimation) {
                                timeoutsRef.current.push(
                                    setTimeout(() => {
                                        sendAdobeEvent({
                                            event: 'interaction',
                                            interaction: { eventID: `${getPageId()}_ClickPlayVideo` },
                                            content: {
                                                race_no: `${date?.replaceAll('-', '')}-${venue}-${raceNo}`,
                                                category: mode
                                            }
                                        });
                                    }, 5000)
                                );
                                setShowButtons({ last400m: true, last200m: true, viewResult: true, skipReplay: false });
                                setIsStart(true);
                            }

                            break;
                        }
                        case 20: {
                            if (!skipAnimation) {
                                setShowButtons({
                                    last400m: false,
                                    last200m: false,
                                    viewResult: false,
                                    skipReplay: true
                                });
                            }

                            break;
                        }
                        case 40: {
                            saveResultToStorage({
                                crossingLineImageUrl: webglTextureToBase64(
                                    gl,
                                    vendorAPIs.getResultScreen(),
                                    1920,
                                    1080
                                ),
                                result: this._gResult,
                                resultVideoUrl: raceData,
                                rsShowType: mode
                            }).finally(() => {
                                // Adobe Launch has a 600ms debounce internally. If two events are fired within 600ms, only the last one will be recorded.
                                // In here, clicking skip replay will fire a WA event, and receiving the race sim result in window.opener will also fire a WA event.
                                // It is found that the two WA events may be too close to each other sometimes, which might be less than 600ms
                                // always delay notifying window.opener "race sim is finished" at least 1 sec to workaround the issue
                                timeoutsRef.current.push(
                                    setTimeout(
                                        () => {
                                            localStorage.setItem(
                                                'raceSimResult',
                                                JSON.stringify({
                                                    reason: 'completed',
                                                    errorCode: this._gErrorNo,
                                                    meetingId,
                                                    raceNo: parseInt(raceNo),
                                                    date,
                                                    venue
                                                })
                                            );
                                        },
                                        Math.min(window.RaceSimulatorConfig.playAnimationAutoCloseDelay / 2, 1000)
                                    )
                                );

                                timeoutsRef.current.push(
                                    setTimeout(
                                        () => !sessionStorage.getItem('rsVideoNotAutoClose') && window.close(),
                                        window.RaceSimulatorConfig.playAnimationAutoCloseDelay
                                    )
                                );
                            });

                            break;
                        }
                        default: {
                            // 1st horse reaches X meters
                            if (this._gStatus >= 100) {
                                if (this._gStatus >= distance - 400) {
                                    setShowButtons((s) => {
                                        return {
                                            last400m: false,
                                            last200m: false,
                                            viewResult: s.viewResult,
                                            skipReplay: false
                                        };
                                    });
                                }

                                if (this._gStatus >= distance) {
                                    setShowButtons({
                                        last400m: false,
                                        last200m: false,
                                        viewResult: false,
                                        skipReplay: false
                                    });
                                }
                            }
                        }
                    }
                }
            });
            Object.defineProperty(window, 'gResult', {
                get() {
                    return this._gResult;
                },
                set(value) {
                    this._gResult = value;

                    // race complete

                    if (!this._gResult) return;

                    setShowButtons({ last400m: false, last200m: false, viewResult: false, skipReplay: false });

                    // When we skip animation,
                    // the reason we don't listen on status === 30 is because status will never be 30 for some reasons
                    // it will instead jump to 20 directly
                    if (skipAnimation) {
                        saveResultToStorage({
                            crossingLineImageUrl: '',
                            result: this._gResult,
                            resultVideoUrl: raceData,
                            rsShowType: mode
                        }).finally(() => {
                            const remainingDelay =
                                window.RaceSimulatorConfig.skipAnimationMinLoadingTime -
                                (Date.now() - startTimeRef.current);
                            const writeResultAndCloseWindow = () => {
                                localStorage.setItem(
                                    'raceSimResult',
                                    JSON.stringify({
                                        reason: 'completed',
                                        errorCode: this._gErrorNo,
                                        meetingId,
                                        raceNo: parseInt(raceNo),
                                        date,
                                        venue
                                    })
                                );

                                !sessionStorage.getItem('rsVideoNotAutoClose') && window.close();
                            };

                            if (remainingDelay > 0) {
                                timeoutsRef.current.push(setTimeout(writeResultAndCloseWindow, remainingDelay));
                                return;
                            }

                            writeResultAndCloseWindow();
                        });
                    }
                }
            });
            Object.defineProperty(window, 'gErrorNo', {
                get() {
                    return this._gErrorNo;
                },
                set(value) {
                    if (value === this.gErrorNo) return;
                    this._gErrorNo = value;
                    setErrorCode(this._gErrorNo);
                }
            });

            return () => {
                Object.keys(vendorAPIs).forEach((key) => delete window[key]);
                timeoutsRef.current.forEach(clearTimeout);
            };
        }
    }, [
        date,
        distance,
        meetingId,
        mode,
        raceData,
        raceNo,
        saveResultToStorage,
        sendAdobeEvent,
        sendMessage,
        skipAnimation,
        venue
    ]);

    useEffect(() => {
        if (isLoaded) {
            sendMessage('GameObject', 'RaceStart', raceData);
        }
    }, [isLoaded, raceData, sendMessage]);

    useEffect(() => {
        const { isMobile, isTablet } = getDeviceType();
        if (isMobile || isTablet) {
            const handleVisibilityChange = () => {
                // prevent page refresh from calling window.close()
                timeoutsRef.current.push(
                    setTimeout(() => {
                        if (document.hidden) window.close();
                    }, 0)
                );
            };
            window.addEventListener('visibilitychange', handleVisibilityChange);
            return () => window.removeEventListener('visibilitychange', handleVisibilityChange);
        }
    }, []);

    const secondaryButtons = (
        <>
            {showButtons.last400m && (
                <Button
                    className={styles.outlineButton}
                    variant="outlined"
                    themeColor="#ffffff"
                    onClick={() => {
                        try {
                            window.JumpLast400();
                            setShowButtons({ last400m: false, last200m: false, viewResult: true, skipReplay: false });
                            sendAdobeEvent({
                                event: 'interaction',
                                interaction: { eventID: 'eWinRacing_HorseSelector-Video_ClickLast400' },
                                content: { category: mode, race_no: `${date?.replaceAll('-', '')}-${venue}-${raceNo}` }
                            });
                        } catch (error) {
                            console.error(error);
                        }
                    }}
                >
                    {t('RS_LAST_400M')}
                </Button>
            )}
            {showButtons.last200m && (
                <Button
                    className={styles.outlineButton}
                    variant="outlined"
                    themeColor="#ffffff"
                    onClick={() => {
                        try {
                            window.JumpLast200();
                            setShowButtons({ last400m: false, last200m: false, viewResult: true, skipReplay: false });
                            sendAdobeEvent({
                                event: 'interaction',
                                interaction: { eventID: 'eWinRacing_HorseSelector-Video_ClickLast200' },
                                content: { category: mode, race_no: `${date?.replaceAll('-', '')}-${venue}-${raceNo}` }
                            });
                        } catch (error) {
                            console.error(error);
                        }
                    }}
                >
                    {t('RS_LAST_200M')}
                </Button>
            )}
        </>
    );

    return (
        // WORKAROUND: SQ0248-6365 iOS Chrome doesn't respect 100dvh
        <div style={{ maxHeight: window.innerHeight }} className={styles.container} {...getTriggerProps()}>
            {skipAnimation && (
                <div className={styles.skipAnimation}>
                    {shouldPlayRaceSim(errorCode) ? (
                        <>
                            <img src={horseRunAnimation} width={100} alt="" />
                            {t('LB_RS_LOADING_SIMULATION_RESULT')}
                        </>
                    ) : (
                        <div className={styles.errorCon}>
                            <div className={styles.error}>
                                <img src={errorBlack} width={56} alt="" />
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: t('RS_SIMULATION_ERROR').replace('XXXX', errorCode)
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            <header className={`${styles.header} ${!visible ? styles.hide : ''}`}>
                {t('RS_RACE_N_SIMULATION').replace('{n}', raceNo)}
                <span
                    className={styles.closeButton}
                    onClick={() => {
                        window.close();
                    }}
                ></span>
            </header>

            {shouldPlayRaceSim(errorCode) ? (
                <div
                    id="unity-container"
                    className={styles.content}
                    style={{ display: skipAnimation ? 'none' : undefined }}
                >
                    <Unity
                        ref={canvasRef}
                        unityProvider={unityProvider}
                        id="unity-canvas"
                        tabIndex={-1}
                        className={styles.unityCanvas}
                    />

                    <div
                        id="unity-loading-bar"
                        className={styles.unityLoadingOverlay}
                        style={{ display: loadingProgression === 1 ? 'none' : 'flex' }}
                    >
                        {/* forcefully change the aspect ratio of the loading image, such that it will be as close to the loading image rendered by WebGL as possible */}
                        <img src={loadingLogo} alt="loading" width="10%" height="12.5%" id="unity-logo" />
                        <div id="unity-progress-bar-empty">
                            <div id="unity-progress-bar-full" />
                        </div>
                    </div>
                </div>
            ) : (
                <div className={styles.error}>
                    <img src={errorIcon} width={56} alt="" />
                    <div dangerouslySetInnerHTML={{ __html: t('RS_SIMULATION_ERROR').replace('XXXX', errorCode) }} />
                </div>
            )}

            <div className={`${styles.secondaryButtonsMobileContainer} ${!visible ? styles.hide : ''}`}>
                {secondaryButtons}
            </div>

            <footer className={`${styles.footer} ${!visible ? styles.hide : ''}`}>
                {secondaryButtons}
                {showButtons.viewResult && (
                    <Button
                        themeColor="#122C68"
                        onClick={() => {
                            try {
                                window.JumpToResult();
                                setShowButtons({
                                    last400m: false,
                                    last200m: false,
                                    viewResult: false,
                                    skipReplay: false
                                });
                                sendAdobeEvent({
                                    event: 'interaction',
                                    interaction: { eventID: 'eWinRacing_HorseSelector-Video_ClickViewResult' },
                                    content: {
                                        category: mode,
                                        race_no: `${date?.replaceAll('-', '')}-${venue}-${raceNo}`
                                    }
                                });
                            } catch (error) {
                                console.error(error);
                            }
                        }}
                    >
                        {t('RS_TD_JUMP_TO_RESULT')}
                    </Button>
                )}
                {showButtons.skipReplay && (
                    <Button
                        themeColor="#122C68"
                        onClick={() => {
                            try {
                                window.SkipReplay();

                                sendAdobeEvent({
                                    event: 'interaction',
                                    interaction: { eventID: 'eWinRacing_HorseSelector-Video_ClickSkip' },
                                    content: {
                                        category: mode,
                                        race_no: `${date?.replaceAll('-', '')}-${venue}-${raceNo}`
                                    }
                                });
                            } catch (error) {
                                console.error(error);
                            }
                        }}
                    >
                        {t('RS_TD_SKIP_REPLAY')}
                    </Button>
                )}
            </footer>
        </div>
    );
};

export const useRaceSimUnityWebGLPopUp = () => {
    const { rsRaceData, rsShowType } = useContext(GlobalContext);
    const {
        content: { date, venue, raceNo, meeting }
    } = useContext(RacingContext);
    const { i18n } = useTranslation();

    const open = useCallback(
        (raceDataArg, mode) => {
            const raceData = raceDataArg ?? rsRaceData;
            const skipAnimation = raceData.charAt(1) === '1';
            console.debug({ raceData });

            const url = new URL(
                `${window.location.origin}/${i18n.language}/race-sim/${date}/${venue}/${raceNo}/${encodeURIComponent(
                    raceData
                )}`
            );
            url.searchParams.set('mode', mode ?? rsShowType);
            url.searchParams.set('meetingId', meeting.id);
            url.searchParams.set('distance', meeting.races.find((race) => race.no === raceNo)?.distance);
            url.searchParams.set('userPreference', `VID:${skipAnimation ? 'N' : 'Y'}`);

            const popupSize = skipAnimation ? { width: 400, height: 225 } : { width: 902, height: 684 };

            return window.open(
                url.toString(),
                'raceSim',
                [
                    `width=${popupSize.width}`,
                    `height=${popupSize.height}`,
                    `top=${(window.screen.availHeight - popupSize.height) / 2}`,
                    `left=${(window.screen.availWidth - popupSize.width) / 2}`,
                    'scrollbars=no',
                    'resizable=no'
                ].join(',')
            );
        },
        [date, i18n.language, meeting.id, meeting.races, raceNo, rsRaceData, rsShowType, venue]
    );

    const prefetch = useCallback(() => {
        const config = getConfig(venue);
        const abortController = new AbortController();

        return {
            promise: Promise.all(
                [config.loaderUrl, config.dataUrl, config.frameworkUrl, config.codeUrl].map(async (url) => {
                    try {
                        return await fetch(url, { priority: 'low', signal: abortController.signal });
                    } catch (error) {
                        if (error.name !== 'AbortError') throw error;
                    }
                })
            ),
            abort: () => abortController.abort()
        };
    }, [venue]);

    return { open, prefetch };
};

export const useWatchRaceSimResult = ({ disabled = false, onChange } = {}) => {
    useEffect(() => {
        if (disabled) return;

        const storageListener = (event) => {
            if (event.storageArea === localStorage && event.key === 'raceSimResult') {
                window.removeEventListener('storage', storageListener);
                localStorage.removeItem('raceSimResult');
                onChange?.(JSON.parse(event.newValue));
            }
        };
        window.addEventListener('storage', storageListener);

        return () => window.removeEventListener('storage', storageListener);
    }, [disabled, onChange]);
};

/**
 * Synced with Business that certain error codes should be ignored
 * 0 - Success
 * 4003 - Class data error
 * 4008 - Horse skin data error
 * 4010 - Jockey cloth color data error
 * @param {number} errorCode
 */
export const shouldPlayRaceSim = (errorCode) => [0, 4003, 4008, 4010].includes(errorCode);

const getConfig = (venue) => ({
    // #if USE_DATA_CACHING
    cacheControl: (url) => {
        // Caching enabled for .data and .bundle files.
        // Revalidate if file is up to date before loading from cache
        if (
            url.match(/\.data/) ||
            url.match(/\.wasm/) ||
            url.match(/\.js/) ||
            url.match(/\.png/) ||
            url.match(/\.css/) ||
            url.match(/\.ico/) ||
            url.match(/\.webmanifest/)
        ) {
            return 'must-revalidate';
        }

        // Caching enabled for .mp4 and .custom files
        // Load file from cache without revalidation.
        //  if (url.match(/\.mp4/) || url.match(/\.custom/)) {
        //      return "immutable";
        //  }

        // Disable explicit caching for all other files.
        // Note: the default browser cache may cache them anyway.
        return 'no-store';
    },
    // #endif
    loaderUrl: window.RaceSimulatorConfig.getLoaderUrl(venue),
    ...window.RaceSimulatorConfig.getUnityInstanceConfig(venue)
});

const useAutoHide = ({ timeout = 3000, initialVisible = true, disabled = false, isStart } = {}) => {
    const [visible, setVisible] = useState(initialVisible);
    const timeoutRef = useRef();

    useEffect(() => {
        if (disabled || !isStart) {
            clearTimeout(timeoutRef.current);
            setVisible(initialVisible);
        } else {
            if (isStart) {
                timeoutRef.current = setTimeout(() => setVisible(false), timeout);
            }
        }

        return () => clearTimeout(timeoutRef.current);
    }, [disabled, initialVisible, timeout, isStart]);

    const getTriggerProps = useCallback(() => {
        if (disabled || !isStart) return;

        return {
            onClick: () => {
                clearTimeout(timeoutRef.current);
                setVisible((prev) => !prev);
                timeoutRef.current = setTimeout(() => setVisible(false), timeout);
            }
        };
    }, [disabled, timeout, isStart]);

    return { visible, getTriggerProps };
};

const assertFramebufferComplete = (gl) => {
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    const map = {
        [gl.FRAMEBUFFER_COMPLETE]: 'FRAMEBUFFER_COMPLETE',
        [gl.FRAMEBUFFER_INCOMPLETE_ATTACHMENT]: 'INCOMPLETE_ATTACHMENT',
        [gl.FRAMEBUFFER_INCOMPLETE_MISSING_ATTACHMENT]: 'MISSING_ATTACHMENT',
        [gl.FRAMEBUFFER_INCOMPLETE_DIMENSIONS]: 'INCOMPLETE_DIMENSIONS',
        [gl.FRAMEBUFFER_UNSUPPORTED]: 'UNSUPPORTED'
    };

    if (status !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error(`Framebuffer incomplete: ${map[status] || '0x' + status.toString(16)}`);
    }
};

const createCompleteFBOForTexture = (gl, texture, width, height, target = gl.TEXTURE_2D) => {
    // 0) Ensure texture is initialized and has valid parameters (especially for NPOT)
    gl.bindTexture(target, texture);
    // If not previously allocated, uncomment:
    // gl.texImage2D(target, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    // NPOT-safe parameters (harmless even for POT textures)
    gl.texParameteri(target, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(target, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(target, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(target, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    // 1) Create and bind FBO

    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    // 2) Attach the texture to COLOR_ATTACHMENT0
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, target, texture, 0);
    // 3) (WebGL2) Ensure read buffer is COLOR_ATTACHMENT0
    if (typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext) {
        gl.readBuffer(gl.COLOR_ATTACHMENT0);
    }
    // 4) Check completeness
    assertFramebufferComplete(gl);
    return fbo;
};

const webglTextureToBase64 = (
    gl,
    texture,
    width,
    height,
    {
        target = gl.TEXTURE_2D,
        format = gl.RGBA,
        type = gl.UNSIGNED_BYTE,
        flipY = true,
        mimeType = 'image/jpeg',
        jpegQuality = 0.92
    } = {}
) => {
    if (type !== gl.UNSIGNED_BYTE || format !== gl.RGBA) {
        throw new Error('Helper expects RGBA + UNSIGNED_BYTE. Convert via shader for other formats.');
    }
    const fbo = createCompleteFBOForTexture(gl, texture, width, height, target);
    // Read pixels
    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, format, type, pixels);
    // Clean up GL
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fbo);
    // Write into a 2D canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const imageData = ctx.createImageData(width, height);
    const dst = imageData.data;
    if (!flipY) {
        dst.set(pixels);
    } else {
        const row = width * 4;
        for (let y = 0; y < height; y++) {
            const src = (height - 1 - y) * row;
            dst.set(pixels.subarray(src, src + row), y * row);
        }
    }
    ctx.putImageData(imageData, 0, 0);
    // Base64
    const dataURL =
        mimeType === 'image/jpeg' ? canvas.toDataURL('image/jpeg', jpegQuality) : canvas.toDataURL(mimeType);
    return dataURL;
};
